import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AppConfigService } from 'src/app/services/app.config.service';

// Percentuale massima dello schermo che il popup può occupare (sia width che height)
const MAX_VIEWPORT_PERCENT = 0.90;

export interface PanelState {
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  isMaximized: boolean;
  isGhostMode?: boolean;
  minimizedPosition?: { x: number; y: number }; // Posizione separata quando minimizzato
}

@Component({
  selector: 'app-floating-panel',
  templateUrl: './floating-panel.component.html',
  styleUrls: ['./floating-panel.component.scss']
})
export class FloatingPanelComponent implements OnInit, OnDestroy, AfterViewInit {
  private static get storageKey() { return `${AppConfigService.role}.FloatingPanel`; }
  
  @Input() title = '';
  @Input() minimizedIcon = 'pan_tool';
  @Input() initialWidthPercent = 0.8;
  @Input() initialHeightPercent = 0.8;
  @Input() minWidth = 400;
  @Input() minHeight = 300;
  @Input() showMaximize = true;
  @Input() showMinimize = true;
  
  @Output() closed = new EventEmitter<void>();
  @Output() stateChanged = new EventEmitter<PanelState>();
  @Output() initialized = new EventEmitter<void>();
  
  @ViewChild('panel', { static: false }) panelElement!: ElementRef<HTMLDivElement>;
  
  isMinimized = false;
  isMaximized = false;
  isGhostMode = false;

  private stateBeforeMaximize?: PanelState;
  private stateBeforeMinimize?: PanelState;
  public minimizedDragPosition = { x: 0, y: 0 };
  
  // Posizione per drag & drop
  dragPosition = { x: 0, y: 0 };

  constructor(public config: AppConfigService) {}
  
  ngOnInit(): void {
    // Carica lo stato salvato o inizializza con valori default
    this.loadState();
  }
  
  ngOnDestroy(): void {
    // Solo cleanup, niente salvataggio qui
  }
  
  ngAfterViewInit(): void {
    // Inizializza le dimensioni
    this.initializeDimensions();
    
    // Notifica che il pannello è stato inizializzato
    setTimeout(() => {
      this.initialized.emit();
    }, 50);
  }
  
  private initializeDimensions(): void {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return;

    const savedState = this.getSavedState();

    if (savedState) {
      if (savedState.isMaximized) {
        // Il savedState contiene sia il flag isMaximized che le dimensioni normali
        // Apri fullscreen ma salva le dimensioni normali in stateBeforeMaximize
        // in modo che quando l'utente toglie il fullscreen, sappiamo dove tornare
        const normalState = { ...savedState };
        normalState.isMaximized = false;

        // Applica il limite 85% alle dimensioni normali salvate
        this.stateBeforeMaximize = this.constrainDimensionsToViewport(normalState);

        // Ora apri fullscreen
        this.isMaximized = true;
        panel.style.top = '0';
        panel.style.left = '0';
        panel.style.width = 'min(100dvw, 100vw)';
        panel.style.height = 'min(100dvh, 100vh)';
        this.dragPosition = { x: 0, y: 0 };
      } else {
        // Ripristina stato salvato completo (posizione E dimensioni)
        // Ma verifica che le dimensioni entrino nello schermo
        const adjustedState = this.constrainDimensionsToViewport(savedState);
        this.applyState(adjustedState);
      }
    } else {
      // Calcola dimensioni iniziali solo se non c'è uno stato salvato
      const defaultDims = this.getDefaultDimensions();

      // Assicurati che la posizione iniziale sia valida
      const constrainedPosition = this.constrainPosition(defaultDims.x, defaultDims.y);

      panel.style.width = `${defaultDims.width}px`;
      panel.style.height = `${defaultDims.height}px`;
      panel.style.left = `${constrainedPosition.x}px`;
      panel.style.top = `${constrainedPosition.y}px`;

      this.dragPosition = { x: 0, y: 0 };
    }

    if (this.config.isMobilePhone) { //parte sempre e solo massimizzato su mobile
      this.applyMaximizeState();
    }
  }
  
  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;

    if (this.isMinimized) {
      // Salva lo stato corrente prima di minimizzare
      this.stateBeforeMinimize = this.getCurrentState();
      this.stateBeforeMinimize.isMinimized = false;

      if (this.isMaximized) {
        this.stateBeforeMinimize.isMaximized = true;
      }

      this.isMaximized = false;

      // Posiziona il pannello minimizzato dopo che il CSS è stato applicato
      setTimeout(() => {
        const panel = this.panelElement?.nativeElement;
        if (!panel) return;

        const savedState = this.getSavedState();
        let minimizedPos: { x: number; y: number };

        if (savedState?.minimizedPosition) {
          minimizedPos = this.constrainMinimizedPosition(savedState.minimizedPosition.x, savedState.minimizedPosition.y);
        } else {
          minimizedPos = this.getDefaultMinimizedPosition();
        }

        panel.style.left = `${minimizedPos.x}px`;
        panel.style.top = `${minimizedPos.y}px`;
        panel.style.transform = '';
      }, 0);

      this.minimizedDragPosition = { x: 0, y: 0 };
    } else {
      // Ripristina lo stato precedente alla minimizzazione
      const panel = this.panelElement?.nativeElement;
      if (!panel) return;

      // Salva la posizione corrente del minimizzato prima di ripristinare
      const currentMinimizedPosition = {
        x: parseFloat(panel.style.left || '0'),
        y: parseFloat(panel.style.top || '0')
      };
      this.saveMinimizedPosition(currentMinimizedPosition);

      // Reset transform
      panel.style.transform = '';
      this.dragPosition = { x: 0, y: 0 };
      this.minimizedDragPosition = { x: 0, y: 0 };

      if (this.stateBeforeMinimize) {
        if (this.stateBeforeMinimize.isMaximized) {
          // Ripristina a fullscreen
          this.isMaximized = true;
          panel.style.top = '0';
          panel.style.left = '0';
          panel.style.width = 'min(100dvw, 100vw)';
          panel.style.height = 'min(100dvh, 100vh)';
        } else {
          // Ripristina le dimensioni normali
          const stateToRestore = { ...this.stateBeforeMinimize };
          stateToRestore.isMinimized = false;
          this.applyState(stateToRestore);
        }
        this.stateBeforeMinimize = undefined;
      } else {
        // Fallback: usa lo stato salvato
        const savedState = this.getSavedState();
        if (savedState) {
          const stateToRestore = { ...savedState };
          stateToRestore.isMinimized = false;
          this.applyState(stateToRestore);
        }
      }
    }

    this.emitStateChange();
  }
  
  toggleMaximize(): void {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return;

    this.isMaximized = !this.isMaximized;

    if (this.isMaximized) {
      // Se era minimizzato, salva la sua posizione prima di massimizzare
      if (this.isMinimized) {
        const currentMinimizedPosition = {
          x: parseFloat(panel.style.left || '0'),
          y: parseFloat(panel.style.top || '0')
        };
        this.saveMinimizedPosition(currentMinimizedPosition);
      }

      this.isMinimized = false;

      // Salva stato corrente in memoria
      this.stateBeforeMaximize = this.getCurrentState();
      this.stateBeforeMaximize.isMaximized = false;

      this.applyMaximizeState();
    } else {
      // Ripristina stato precedente
      panel.style.transform = '';
      this.dragPosition = { x: 0, y: 0 };

      if (this.stateBeforeMaximize) {
        const constrainedState = this.constrainDimensionsToViewport(this.stateBeforeMaximize);
        this.applyState(constrainedState);
        this.stateBeforeMaximize = undefined;
      } else {
        // Fallback: usa dimensioni di default
        const defaultDims = this.getDefaultDimensions();
        panel.style.width = `${defaultDims.width}px`;
        panel.style.height = `${defaultDims.height}px`;
        panel.style.left = `${defaultDims.x}px`;
        panel.style.top = `${defaultDims.y}px`;
      }
    }

    this.emitStateChange();
  }

  private applyMaximizeState(): void {
      const panel = this.panelElement?.nativeElement;
      if (!panel) return;
      this.isMaximized = true;
      // Massimizza
      panel.style.top = '0';
      panel.style.left = '0';
      panel.style.width = 'min(100dvw, 100vw)';
      panel.style.height = 'min(100dvh, 100vh)';
      this.dragPosition = { x: 0, y: 0 };
  }
  
  onDragEnded(event: CdkDragEnd): void {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return;

    // Calcola la nuova posizione dal transform
    const transform = panel.style.transform;
    const matrix = new DOMMatrix(transform);
    const currentLeft = parseFloat(panel.style.left || '0');
    const currentTop = parseFloat(panel.style.top || '0');
    const newLeft = currentLeft + matrix.m41;
    const newTop = currentTop + matrix.m42;

    // Applica i limiti di posizione
    const constrainedPosition = this.isMinimized
      ? this.constrainMinimizedPosition(newLeft, newTop)
      : this.constrainPosition(newLeft, newTop);

    panel.style.left = `${constrainedPosition.x}px`;
    panel.style.top = `${constrainedPosition.y}px`;
    panel.style.transform = '';

    // Reset drag position
    if (this.isMinimized) {
      this.minimizedDragPosition = { x: 0, y: 0 };
      this.saveMinimizedPosition(constrainedPosition);
    } else {
      this.dragPosition = { x: 0, y: 0 };
    }
  }
  
  onClose(): void {
    // Salva lo stato prima di chiudere
    this.saveState();
    this.closed.emit();
  }
  
  onHeaderDoubleClick(): void {

    return; //#98336 fix problema trasparenza

    // Toggle ghost mode
    this.isGhostMode = !this.isGhostMode;
    this.emitStateChange();
  }
  
  private getCurrentState(): PanelState {
    const panel = this.panelElement?.nativeElement;
    if (!panel) {
      return {
        position: { x: 0, y: 0 },
        size: { width: this.minWidth, height: this.minHeight },
        isMinimized: this.isMinimized,
        isMaximized: this.isMaximized
      };
    }

    const savedState = this.getSavedState();

    return {
      position: {
        x: parseFloat(panel.style.left || '0'),
        y: parseFloat(panel.style.top || '0')
      },
      size: {
        width: panel.offsetWidth,
        height: panel.offsetHeight
      },
      isMinimized: this.isMinimized,
      isMaximized: this.isMaximized,
      isGhostMode: this.isGhostMode,
      minimizedPosition: savedState?.minimizedPosition // Mantieni la posizione minimizzata salvata
    };
  }

  private saveMinimizedPosition(position: { x: number; y: number }): void {
    try {
      let savedState = this.getSavedState();
      if (!savedState) {
        savedState = this.getCurrentState();
      }
      savedState.minimizedPosition = position;
      localStorage.setItem(FloatingPanelComponent.storageKey, JSON.stringify(savedState));
    } catch (e) {
      console.error('Failed to save minimized position:', e);
    }
  }
  
  private applyState(state: PanelState): void {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return;
    
    // Usa la funzione di constraint per verificare la posizione
    const constrainedPosition = this.constrainPosition(state.position.x, state.position.y);
    
    let width = state.size.width;
    let height = state.size.height;
    
    // Applica dimensioni minime
    width = Math.max(width, this.minWidth);
    height = Math.max(height, this.minHeight);
    
    // Limita anche le dimensioni massime
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    width = Math.min(width, viewportWidth);
    height = Math.min(height, viewportHeight);
    
    panel.style.left = `${constrainedPosition.x}px`;
    panel.style.top = `${constrainedPosition.y}px`;
    panel.style.width = `${width}px`;
    panel.style.height = `${height}px`;
    
    this.isMinimized = state.isMinimized;
    this.isMaximized = state.isMaximized;
    this.isGhostMode = state.isGhostMode || false;
  }
  
  private saveState(): void {
    // Non salvare se minimizzato (lo stato minimizzato è temporaneo)
    if (this.isMinimized) return;

    let stateToSave: PanelState;

    if (this.isMaximized && this.stateBeforeMaximize) {
      // Se chiudiamo mentre siamo fullscreen, salva le dimensioni normali che avevamo prima
      // ma mantieni il flag isMaximized per riaprire fullscreen
      stateToSave = { ...this.stateBeforeMaximize };
      stateToSave.isMaximized = true; // Ricorda che eravamo fullscreen
    } else {
      // Altrimenti salva lo stato corrente
      stateToSave = this.getCurrentState();
    }

    try {
      localStorage.setItem(FloatingPanelComponent.storageKey, JSON.stringify(stateToSave));
    } catch (e) {
      console.error('Failed to save panel state:', e);
    }
  }
  
  private loadState(): void {
    const savedState = this.getSavedState();
    if (savedState) {
      this.isMinimized = savedState.isMinimized;
      this.isMaximized = savedState.isMaximized;
      this.isGhostMode = savedState.isGhostMode || false;
    }
  }
  
  private getSavedState(): PanelState | null {
    try {
      const saved = localStorage.getItem(FloatingPanelComponent.storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error('Failed to load panel state:', e);
      return null;
    }
  }
  
  private emitStateChange(): void {
    this.stateChanged.emit(this.getCurrentState());
  }
  
  @HostListener('window:resize')
  onWindowResize(): void {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return;

    if (this.isMinimized) {
      // Se minimizzato, verifica che sia ancora visibile dopo il resize
      const currentX = parseFloat(panel.style.left || '0');
      const currentY = parseFloat(panel.style.top || '0');

      // Aspetta che il resize sia completato prima di controllare
      setTimeout(() => {
        const constrainedPosition = this.constrainMinimizedPosition(currentX, currentY);

        // Se la posizione è cambiata (era fuori schermo), aggiorna
        if (constrainedPosition.x !== currentX || constrainedPosition.y !== currentY) {
          panel.style.left = `${constrainedPosition.x}px`;
          panel.style.top = `${constrainedPosition.y}px`;
          this.saveMinimizedPosition(constrainedPosition);
        }
      }, 0);
    } else if (!this.isMaximized) {
      // Per pannello normale, applica il limite 90% se necessario
      const state = this.getCurrentState();
      const constrainedState = this.constrainDimensionsToViewport(state);
      this.applyState(constrainedState);
    }
  }

  private getDefaultMinimizedPosition(): { x: number; y: number } {
    const panel = this.panelElement?.nativeElement;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Usa dimensioni reali del pannello se disponibile
    const minimizedWidth = panel?.offsetWidth || 200;
    const minimizedHeight = panel?.offsetHeight || 40;

    // Posiziona in basso a destra con margine di 20px
    const margin = 20;
    return {
      x: viewportWidth - minimizedWidth - margin,
      y: viewportHeight - minimizedHeight - margin
    };
  }

  private constrainMinimizedPosition(x: number, y: number): { x: number; y: number } {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return { x, y };

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minimizedWidth = panel.offsetWidth;
    const minimizedHeight = panel.offsetHeight;

    // Se la posizione è completamente fuori schermo, usa default
    if (x < -viewportWidth || x > viewportWidth * 2 ||
        y < -viewportHeight || y > viewportHeight * 2) {
      return this.getDefaultMinimizedPosition();
    }

    // Mantieni il pannello completamente visibile
    const constrainedX = Math.max(0, Math.min(x, viewportWidth - minimizedWidth));
    const constrainedY = Math.max(0, Math.min(y, viewportHeight - minimizedHeight));

    return { x: constrainedX, y: constrainedY };
  }

  private getDefaultDimensions(): { width: number; height: number; x: number; y: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calcola le dimensioni desiderate (usa initialWidthPercent/initialHeightPercent)
    let width = Math.max(viewportWidth * this.initialWidthPercent, this.minWidth);
    let height = Math.max(viewportHeight * this.initialHeightPercent, this.minHeight);

    // Limita alla percentuale massima dello schermo se necessario
    const maxWidth = viewportWidth * MAX_VIEWPORT_PERCENT;
    const maxHeight = viewportHeight * MAX_VIEWPORT_PERCENT;

    width = Math.min(width, maxWidth);
    height = Math.min(height, maxHeight);

    // Assicura dimensioni minime
    width = Math.max(width, this.minWidth);
    height = Math.max(height, this.minHeight);

    // Centra il pannello
    const x = (viewportWidth - width) / 2;
    const y = (viewportHeight - height) / 2;

    return { width, height, x, y };
  }

  private constrainDimensionsToViewport(state: PanelState): PanelState {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Limita le dimensioni alla percentuale massima dello schermo
    const maxWidth = viewportWidth * MAX_VIEWPORT_PERCENT;
    const maxHeight = viewportHeight * MAX_VIEWPORT_PERCENT;

    const adjustedState = { ...state };

    // Ridimensiona se necessario, mantenendo le proporzioni
    if (adjustedState.size.width > maxWidth || adjustedState.size.height > maxHeight) {
      // Calcola il fattore di scala per mantenere le proporzioni
      const scaleWidth = maxWidth / adjustedState.size.width;
      const scaleHeight = maxHeight / adjustedState.size.height;
      const scale = Math.min(scaleWidth, scaleHeight);

      adjustedState.size.width = Math.max(adjustedState.size.width * scale, this.minWidth);
      adjustedState.size.height = Math.max(adjustedState.size.height * scale, this.minHeight);

      // Ricentra il popup dopo il ridimensionamento
      adjustedState.position.x = (viewportWidth - adjustedState.size.width) / 2;
      adjustedState.position.y = (viewportHeight - adjustedState.size.height) / 2;
    }

    return adjustedState;
  }

  private constrainPosition(x: number, y: number): { x: number; y: number } {
    const panel = this.panelElement?.nativeElement;
    if (!panel) return { x, y };

    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Per pannello normale, mantieni almeno il 50% visibile orizzontalmente
    const minVisibleWidth = panelWidth * 0.5;
    const headerHeight = 40;

    let constrainedX = x;
    let constrainedY = y;

    // Limiti orizzontali: almeno il 50% del pannello deve essere visibile
    if (constrainedX + minVisibleWidth < 0) {
      constrainedX = -panelWidth + minVisibleWidth;
    } else if (constrainedX + panelWidth - minVisibleWidth > viewportWidth) {
      constrainedX = viewportWidth - minVisibleWidth;
    }

    // Limiti verticali: header sempre accessibile
    constrainedY = Math.max(0, constrainedY);
    if (constrainedY + headerHeight > viewportHeight) {
      constrainedY = viewportHeight - headerHeight;
    }

    return { x: constrainedX, y: constrainedY };
  }
}
